/* eslint-disable @typescript-eslint/no-explicit-any */
import { HotelImageType } from "data/travel-agency/customer/hotelDetails";
import { Col, Container, Row, Spinner } from "react-bootstrap";

import ThermalAnomalyEquipmentInfo from "./ThermalAnomalyEquipmentInfo";
import ThermalAnomalyImages from "./ThermalAnomalyImages";

import { default as img1, default as largeImg1 } from "assets/img/thermography/IR_1629.jpg";
import { default as img2, default as largeImg2 } from "assets/img/thermography/IR_1630.jpg";
import { default as img3, default as largeImg3 } from "assets/img/thermography/isolador_1.jpg";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { v4 as uuidv4 } from 'uuid';

import { ModeFormProps } from "./Utils";
import { AnomalyRecord, anomalyRecordDefaultValue } from "data/entities/AnomalyRecord";
import { IThermogram, createDefaultThermogram } from "pages/thermography/model/thermogram.model";
import { DatabaseService } from "./stores/DatabaseService";
import { useToast } from "pages/thermography/components/ToastProvider";
import { IEquipment } from "./model/equipment.model";
import { IInspectionRecord } from "./model/inspection-record.model";
import { useAppSelector } from 'pages/thermography/config/store';

export const thermalImages: HotelImageType[] = [
    { id: 1, img: img1, largeImg: largeImg1, classNames: "col-span-12 col-span-md-6 row-span-md-2" },
    { id: 2, img: img2, largeImg: largeImg2, classNames: "col-span-12 col-span-md-6 row-span-md-2" },
    { id: 3, img: img3, largeImg: largeImg3, classNames: "col-span-12 col-span-md-6 row-span-md-2" },
];

const ThermalAnomalyForm = ({ mode }: ModeFormProps) => {
    const dbService = DatabaseService.getInstance();
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });

    const { codigo, plantId, equipmentId, routeId } = useParams<{
        codigo?: string;
        plantId?: string;
        equipmentId?: string;
        routeId?: string;
    }>();

    const navigate = useNavigate();
    const toast = useToast();

    const [dbReady, setDbReady] = useState(false);
    const [anomalyRecords, setAnomalyRecords] = useState<AnomalyRecord[]>([]);

    const [anomalyRecord, setAnomalyRecord] = useState<AnomalyRecord>(anomalyRecordDefaultValue);
    const [thermogram, setThermogram] = useState<IThermogram>(createDefaultThermogram());
    const [thermogramRef, setThermogramRef] = useState<IThermogram>(createDefaultThermogram());
    const [deltaT, setDeltaT] = useState<number>(0);

    
    const user = useAppSelector(state => state.authentication.account);
    const thermographers = dbService.getThermographers();
    const userInfo = thermographers?.find(thermographer => thermographer.user?.id === user?.id);

    const buildNewTrrRecord = (plantId?: string, equipmentId?: string, routeId?: string): AnomalyRecord => {
        const base: AnomalyRecord = {
            ...anomalyRecordDefaultValue,
            // anomalyRecordList: [...(anomalyRecordDefaultValue.anomalyRecordList ?? [])],
        };

        return {
            ...base,
            plant: {
                ...(base.plant ?? { id: "", name: "", code: "" }),
                id: plantId ?? base.plant?.id ?? "",
            },
            equipment: equipmentId ? ({ ...(base.equipment ?? {}), id: equipmentId } as IEquipment) : base.equipment ?? null,
            route: routeId ? ({ ...(base.route ?? {}), id: routeId } as IInspectionRecord) : base.route ?? null,
            createdBy: userInfo ? userInfo : undefined,
        };
    };

    const resolveAnomalyRecord = (records: AnomalyRecord[]): AnomalyRecord => {
        switch (mode) {
            case "new":
                return { 
                    ...anomalyRecordDefaultValue,
                    id: uuidv4(),
                    createdBy: userInfo ? userInfo : undefined };

            case "new_trr":
                return buildNewTrrRecord(plantId, equipmentId, routeId);

            case "edit":
            case "view":
            case "edit_trr":
                return records.find((x) => String(x.id) === String(codigo)) ?? anomalyRecordDefaultValue;

            default:
                return anomalyRecordDefaultValue;
        }
    };

    const resolveThermogram = (rec: AnomalyRecord): IThermogram =>
        rec.thermogram ? rec.thermogram : createDefaultThermogram();

    const resolveThermogramRef = (rec: AnomalyRecord): IThermogram =>
        rec.thermogramRef ? rec.thermogramRef : createDefaultThermogram();

    //Inicializa o DB ao montar (ou quando a rota mudar) e carrega anomalyRecords
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                if (!dbService.isInitialized) {
                    await dbService.initialize();
                }
                if (cancelled) return;

                const records = dbService.getAnomalyRecords() ?? [];
                setAnomalyRecords(records);
                setDbReady(true);
            } catch (e) {
                setDbReady(true);
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, []);

    // Sempre que DB estiver pronto ou params/mode mudarem, recalcula o que vai pro form
    useEffect(() => {
        if (!dbReady) return;

        const next = resolveAnomalyRecord(anomalyRecords);

        setAnomalyRecord(next);
        setThermogram(resolveThermogram(next));
        setThermogramRef(resolveThermogramRef(next));
    }, [dbReady, mode, codigo, plantId, equipmentId, routeId, anomalyRecords]);

    const saveAnomalyEquipmentInfo = async () => {
        let response = false;

        const payload: AnomalyRecord = {
            ...anomalyRecord,
            thermogram,
            thermogramRef,
        };

        try {
            if (mode === "new" || mode === "new_trr") {
                response = await dbService.addAnomalyRecord(payload);
            } else if (mode === "edit" || mode === "edit_trr") {
                response = await dbService.updateAnomalyRecord(payload.id, payload);
            }

            if (response) {
                setAnomalyRecords(dbService.getAnomalyRecords() ?? []);
                toast.showToast(
                    tt("pages.thermography.thermalAnomalyForm.toast.successTitle"),
                    tt("pages.thermography.thermalAnomalyForm.toast.successMessage")
                );
                navigate(-1);
            }
        } catch (error) {
            toast.showToast(
                tt("pages.thermography.thermalAnomalyForm.toast.errorTitle"),
                tt("pages.thermography.thermalAnomalyForm.toast.errorMessage")
            );
        }
    };

    if (!dbReady) {
        return (
            <section className="pt-2 pb-2">
                <Container fluid="large">
                    <div className="d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        <span>{tt("pages.thermography.thermalAnomalyForm.loadingData")}</span>
                    </div>
                </Container>
            </section>
        );
    }

    return (
        <section className="pt-2 pb-2">
            <Container fluid="large">
                <Row className="g-3" style={{ width: "100%" }}>
                    <Col xl={4}>
                        <ThermalAnomalyEquipmentInfo
                            className="mt-3 mt-xl-0"
                            mode={mode}
                            anomalyRecord={anomalyRecord}
                            setAnomalyRecord={setAnomalyRecord}
                            saveAnomalyEquipmentInfo={saveAnomalyEquipmentInfo}
                            deltaT={deltaT}
                        />
                    </Col>

                    <Col xl={8}>
                        <ThermalAnomalyImages
                            images={thermalImages}
                            mode={mode}
                            thermogram={thermogram}
                            setThermogram={setThermogram}
                            thermogramRef={thermogramRef}
                            setThermogramRef={setThermogramRef}
                            deltaT={deltaT}
                            setDeltaT={setDeltaT}
                        />
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default ThermalAnomalyForm;

